import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, RefreshCw, Save, Radio } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useCurrentUser } from "@/lib/hooks/auth";
import {
  useGraylogConfig,
  useUpdateGraylogConfig,
  useUploadGraylogCaCert,
} from "@/lib/hooks/admin";
import { useResetMfaErrorOnVerify } from "@/lib/hooks/mfa-verify-prompt";
import { GraylogConfigUpdateSchema, type GraylogConfigUpdateInput } from "@/lib/schemas/admin";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/settings_/system/graylog")({
  component: GraylogConfigPage,
});

function GraylogConfigPage(): JSX.Element {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const query = useGraylogConfig();
  const mutation = useUpdateGraylogConfig();
  const uploadCert = useUploadGraylogCaCert();
  useResetMfaErrorOnVerify(mutation, uploadCert);

  const form = useForm<GraylogConfigUpdateInput>({
    resolver: zodResolver(GraylogConfigUpdateSchema),
    defaultValues: { enabled: false, host: "", port: 12514, verify_ca: "", stream: "dpanel" },
  });

  const cfg = query.data;
  useEffect(() => {
    if (cfg) {
      form.reset({
        enabled: cfg.enabled,
        host: cfg.host,
        port: cfg.port,
        verify_ca: cfg.verify_ca ?? "",
        stream: cfg.stream,
      });
    }
  }, [cfg, form]);

  if (me.data && me.data.role !== "superuser") {
    return (
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight">{t("graylog_config.title")}</h1>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("graylog_config.superuser_only")}
        </div>
      </div>
    );
  }

  const enabled = form.watch("enabled");

  async function onSubmit(values: GraylogConfigUpdateInput): Promise<void> {
    try {
      await mutation.mutateAsync({
        ...values,
        // Trim: a blank/whitespace-only field means "no TLS verify" (null), not a
        // CA path of " " (which the sender would try to load → fail).
        verify_ca: values.verify_ca?.trim() ? values.verify_ca.trim() : null,
      });
    } catch {
      // surfaced via mutation.error
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("graylog_config.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("graylog_config.subtitle")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw className={cn("mr-1.5 size-3.5", query.isFetching && "animate-spin")} aria-hidden="true" />
          {t("graylog_config.refresh")}
        </Button>
      </header>

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("graylog_config.load_error")} — {query.error.message}
        </div>
      )}

      {cfg && (
        <section className="space-y-3 rounded-md border border-border bg-card p-4">
          <header className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Radio className="size-5 text-primary" aria-hidden="true" />
              {t("graylog_config.card_title")}
            </h2>
            <span className="text-xs text-muted-foreground">
              {t("graylog_config.source", { source: t(`graylog_config.source_${cfg.source}`) })}
            </span>
          </header>

          <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
            <div className="flex items-start gap-2">
              <Checkbox
                id="gl_enabled"
                checked={enabled}
                onCheckedChange={(c) => form.setValue("enabled", c === true)}
                disabled={mutation.isPending}
                className="mt-0.5"
              />
              <Label htmlFor="gl_enabled" className="cursor-pointer text-xs font-medium">
                {t("graylog_config.fields.enabled")}
              </Label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="gl_host" className="text-xs font-medium">
                  {t("graylog_config.fields.host")}
                </Label>
                <Input id="gl_host" placeholder="graylog.example.com" disabled={mutation.isPending} {...form.register("host")} />
                {form.formState.errors.host && (
                  <p className="text-xs text-destructive">{t(form.formState.errors.host.message ?? "")}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="gl_port" className="text-xs font-medium">
                  {t("graylog_config.fields.port")}
                </Label>
                <Input
                  id="gl_port"
                  type="number"
                  min={1}
                  max={65535}
                  disabled={mutation.isPending}
                  {...form.register("port", { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="gl_verify_ca" className="text-xs font-medium">
                  {t("graylog_config.fields.verify_ca")}
                </Label>
                <Input
                  id="gl_verify_ca"
                  placeholder="/etc/letsencrypt/live/.../ca.pem"
                  disabled={mutation.isPending}
                  {...form.register("verify_ca")}
                />
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer text-[10px] text-primary hover:underline">
                    {uploadCert.isPending
                      ? t("graylog_config.fields.ca_uploading")
                      : t("graylog_config.fields.ca_upload")}
                    <input
                      type="file"
                      accept=".pem,.crt,.cer,application/x-pem-file,text/plain"
                      className="hidden"
                      disabled={uploadCert.isPending}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f)
                          uploadCert.mutate(f, {
                            onSuccess: (cfg) => form.setValue("verify_ca", cfg.verify_ca ?? ""),
                          });
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
                <p className="text-[10px] text-muted-foreground">{t("graylog_config.fields.verify_ca_hint")}</p>
                {uploadCert.error && (
                  <p className="text-[10px] text-destructive">{formatApiError(uploadCert.error)[0]}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="gl_stream" className="text-xs font-medium">
                  {t("graylog_config.fields.stream")}
                </Label>
                <Input id="gl_stream" disabled={mutation.isPending} {...form.register("stream")} />
              </div>
            </div>

            <div className="rounded-md border border-warning/30 bg-warning/5 p-2 text-[11px] text-muted-foreground">
              {t("graylog_config.worker_note")}
            </div>

            {mutation.error && (
              <ul className="space-y-0.5 text-xs text-destructive" role="alert">
                {formatApiError(mutation.error).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {mutation.isSuccess && !mutation.error && (
              <p className="text-xs text-success">{t("graylog_config.saved")}</p>
            )}

            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={mutation.isPending} className="gap-1.5">
                {mutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="size-3.5" aria-hidden="true" />
                )}
                {t("common.save")}
              </Button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
